import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { FaUser, FaEnvelope, FaLock, FaPhone, FaEye, FaEyeSlash, FaCheckCircle, FaStar } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import images from '../assets/images';
import logo from '../assets/logo.png';
import SEO, { PageSEO } from '../components/SEO';
import BrandName from '../components/BrandName';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required('First name is required'),
      lastName: Yup.string().required('Last name is required'),
      email: Yup.string().email('Invalid email address').required('Email is required'),
      phone: Yup.string().matches(/^[0-9]{10}$/, 'Phone must be 10 digits'),
      password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .required('Password is required'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('Please confirm your password'),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        const { confirmPassword, ...registerData } = values;
        await register(registerData);
        toast.success('Registration successful! Welcome to Fitnession!');
        navigate('/dashboard');
      } catch (error) {
        toast.error(error.response?.data?.message || 'Registration failed. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
  });

  const features = [
    { icon: FaCheckCircle, text: 'Personal health coaching' },
    { icon: FaCheckCircle, text: 'Custom meal & workout plans' },
    { icon: FaCheckCircle, text: '24/7 support from care team' },
  ];

  const stats = [
    { value: '10K+', label: 'Members' },
    { value: '18%', label: 'Avg. Weight Loss' },
    { value: '94%', label: 'Satisfaction' },
  ];

  return (
    <>
      <SEO {...PageSEO.register} />
      <div className="min-h-screen flex">
      {/* Left Side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/90 via-success-900/80 to-primary-800/90 z-10"></div>
        <img
          src={images.auth.register}
          alt="Start Your Journey"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 z-20 flex flex-col justify-start items-center text-white p-8 pt-12 overflow-y-auto">
          <div className="max-w-md my-auto">
            <h2 className="text-4xl font-bold mb-4 text-center">Start Your Transformation</h2>
            <p className="text-xl text-white/80 mb-8 text-center">
              Join thousands who've achieved lasting weight loss with Fitnession
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-4">
                  <div className="text-2xl font-bold text-white">{stat.value}</div>
                  <div className="text-sm text-white/70">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Features */}
            <div className="space-y-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-3">
                  <feature.icon className="text-success-400 flex-shrink-0" />
                  <span className="text-white/90">{feature.text}</span>
                </div>
              ))}
            </div>

            {/* Testimonial */}
            <div className="mt-8 bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="flex items-center mb-3">
                {[...Array(5)].map((_, i) => (
                  <FaStar key={i} className="text-yellow-400 text-sm" />
                ))}
              </div>
              <p className="text-white/90 italic mb-3">
                "I lost 23 kg in 8 months. Fitnession changed my life completely!"
              </p>
              <div className="flex items-center">
                <img
                  src={images.testimonials.person1}
                  alt="Testimonial"
                  className="w-10 h-10 rounded-full object-cover mr-3"
                />
                <div>
                  <p className="text-white font-semibold text-sm">Priya S.</p>
                  <p className="text-white/60 text-xs">Mumbai, India</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8 bg-gray-50 overflow-y-auto">
        <div className="max-w-lg w-full">
          {/* Logo and Header */}
          <div className="text-center mb-6">
            <Link to="/" className="inline-flex items-center space-x-2 mb-4">
              <img src={logo} alt="Fitnession" className="h-10 w-auto" />
              <BrandName className="text-2xl" />
            </Link>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Create Your Account</h2>
            <p className="text-gray-600 text-sm">Start your health transformation today</p>
          </div>

          {/* Registration Form */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <form onSubmit={formik.handleSubmit} className="space-y-4">
              {/* Name Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaUser className="text-gray-400 text-sm" />
                    </div>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      className={`block w-full pl-9 pr-3 py-2.5 text-sm border ${
                        formik.touched.firstName && formik.errors.firstName
                          ? 'border-red-300'
                          : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent`}
                      placeholder="John"
                      {...formik.getFieldProps('firstName')}
                    />
                  </div>
                  {formik.touched.firstName && formik.errors.firstName && (
                    <p className="mt-1 text-xs text-red-600">{formik.errors.firstName}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaUser className="text-gray-400 text-sm" />
                    </div>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      className={`block w-full pl-9 pr-3 py-2.5 text-sm border ${
                        formik.touched.lastName && formik.errors.lastName
                          ? 'border-red-300'
                          : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent`}
                      placeholder="Doe"
                      {...formik.getFieldProps('lastName')}
                    />
                  </div>
                  {formik.touched.lastName && formik.errors.lastName && (
                    <p className="mt-1 text-xs text-red-600">{formik.errors.lastName}</p>
                  )}
                </div>
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaEnvelope className="text-gray-400 text-sm" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    className={`block w-full pl-9 pr-3 py-2.5 text-sm border ${
                      formik.touched.email && formik.errors.email
                        ? 'border-red-300'
                        : 'border-gray-300'
                    } rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent`}
                    placeholder="you@example.com"
                    {...formik.getFieldProps('email')}
                  />
                </div>
                {formik.touched.email && formik.errors.email && (
                  <p className="mt-1 text-xs text-red-600">{formik.errors.email}</p>
                )}
              </div>

              {/* Phone Field */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-gray-400">(Optional)</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaPhone className="text-gray-400 text-sm" />
                  </div>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    className={`block w-full pl-9 pr-3 py-2.5 text-sm border ${
                      formik.touched.phone && formik.errors.phone
                        ? 'border-red-300'
                        : 'border-gray-300'
                    } rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent`}
                    placeholder="1234567890"
                    {...formik.getFieldProps('phone')}
                  />
                </div>
                {formik.touched.phone && formik.errors.phone && (
                  <p className="mt-1 text-xs text-red-600">{formik.errors.phone}</p>
                )}
              </div>

              {/* Password Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaLock className="text-gray-400 text-sm" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      className={`block w-full pl-9 pr-9 py-2.5 text-sm border ${
                        formik.touched.password && formik.errors.password
                          ? 'border-red-300'
                          : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent`}
                      placeholder="Min. 8 characters"
                      {...formik.getFieldProps('password')}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <FaEyeSlash className="text-gray-400 hover:text-gray-600 text-sm" />
                      ) : (
                        <FaEye className="text-gray-400 hover:text-gray-600 text-sm" />
                      )}
                    </button>
                  </div>
                  {formik.touched.password && formik.errors.password && (
                    <p className="mt-1 text-xs text-red-600">{formik.errors.password}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaLock className="text-gray-400 text-sm" />
                    </div>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      className={`block w-full pl-9 pr-3 py-2.5 text-sm border ${
                        formik.touched.confirmPassword && formik.errors.confirmPassword
                          ? 'border-red-300'
                          : 'border-gray-300'
                      } rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent`}
                      placeholder="Confirm password"
                      {...formik.getFieldProps('confirmPassword')}
                    />
                  </div>
                  {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                    <p className="mt-1 text-xs text-red-600">{formik.errors.confirmPassword}</p>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Creating Account...
                  </div>
                ) : (
                  'Create Account'
                )}
              </button>

              {/* Terms */}
              <p className="text-xs text-gray-500 text-center">
                By signing up, you agree to our{' '}
                <Link to="/terms" className="text-primary-600 hover:underline">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-primary-600 hover:underline">Privacy Policy</Link>
              </p>
            </form>

            {/* Sign In Link */}
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
                  Sign in
                </Link>
              </p>
            </div>
          </div>

          {/* Trust Badges */}
          {/* <div className="mt-6 flex items-center justify-center space-x-6 text-xs text-gray-500">
            <div className="flex items-center">
              <FaCheckCircle className="text-success-500 mr-1" />
              <span>HIPAA Compliant</span>
            </div>
            <div className="flex items-center">
              <FaCheckCircle className="text-success-500 mr-1" />
              <span>30-Day Guarantee</span>
            </div>
            <div className="flex items-center">
              <FaCheckCircle className="text-success-500 mr-1" />
              <span>Cancel Anytime</span>
            </div>
          </div> */}
        </div>
      </div>
    </div>
    </>
  );
};

export default Register;
